import * as Sentry from '@sentry/nextjs'
import useCustomer from '@framework/customer/use-customer'
import { TProductDetailSections } from '../../useProductPageVariation'

import { Button } from '@noissue-ui-kit/oldBranding/button'
import { Form } from '@noissue-ui-kit/oldBranding/form'
import { useState } from 'react'
import { FormInput } from '@noissue-ui-kit/oldBranding/formInput'
import { FormCheckbox } from '@noissue-ui-kit/oldBranding/formCheckbox'

type TCtaState = 'Get notified' | 'All set! Thank you!' | 'Something went wrong'

const CTA_STATE: Record<string, TCtaState> = {
  INITIAL: 'Get notified',
  SUCCESS: 'All set! Thank you!',
  FAILURE: 'Something went wrong',
}

export function useComingSoonProductDetailSection(
  productSlug: string
): TProductDetailSections {
  const { data: customer } = useCustomer()
  const [buttonText, setButtonText] = useState<TCtaState>(CTA_STATE.INITIAL)
  const [requestIsInProgress, setRequestIsInProgress] = useState<boolean>(false)
  const priceSection = null

  const onSubmit = async (formData: {
    email: string
    subscribedToNewsletter: boolean
  }) => {
    if (buttonText !== CTA_STATE.INITIAL) {
      return
    }
    const { email } = formData

    try {
      setRequestIsInProgress(true)

      // Added it as a plug for the flow while we decide if we need to decommission it completely
      Sentry.captureMessage(
        `Customer ${email} requested "Back in stock" email for the product ${productSlug}`
      )

      setButtonText(CTA_STATE.SUCCESS)
    } catch (err) {
      setButtonText(CTA_STATE.FAILURE)
      Sentry.captureException(
        new Error(
          `Subscription to "Back In Stock" flow failed with a message: ${err?.message}. Customer's email: ${email}`
        )
      )
    } finally {
      setRequestIsInProgress(false)
    }
  }

  const mainSection = (
    <>
      <h3 className="mb-10 text-4xl font-semibold text-core-tree">
        Coming soon
      </h3>

      <Form onSubmit={onSubmit}>
        <FormInput
          label="Get notified when this product is available."
          type="email"
          name="email"
          placeholder="Enter your email"
          validators={['required', 'email']}
          initialValue={customer?.email || ''}
        />

        <FormCheckbox
          name="subscribedToNewsletter"
          label="Join our newsletter"
          description="For tips, tricks &amp; more on being a sustainable business"
          initialValue={false}
        />

        <Button
          size={'m'}
          corners={'square'}
          className={`w-full ${
            buttonText !== CTA_STATE.INITIAL ? 'pointer-events-none' : ''
          }`}
          colour="purple"
          loading={requestIsInProgress}
        >
          {buttonText}
        </Button>
      </Form>
    </>
  )

  return {
    priceSection,
    mainSection,
  }
}
